import { useEffect, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useBrowseStore } from "@/features/browse";
import { FeedCard } from "@/features/browse/FeedCard";
import { usePreferencesStore } from "@/features/preferences";
import { useTrainingStore } from "@/features/training";
import { useAuthStore } from "@/features/auth";
import { getLanguageName } from "@/lib/language-names";

const SCROLL_KEY = "feed-scroll-y";

export function FeedPage() {
  const coverage = useBrowseStore((s) => s.coverage);
  const loadCoverage = useBrowseStore((s) => s.loadCoverage);
  const feedDialogs = useBrowseStore((s) => s.feedDialogs);
  const feedLoading = useBrowseStore((s) => s.feedLoading);
  const feedLoadingMore = useBrowseStore((s) => s.feedLoadingMore);
  const feedHasMore = useBrowseStore((s) => s.feedHasMore);
  const loadFeed = useBrowseStore((s) => s.loadFeed);
  const loadMoreFeed = useBrowseStore((s) => s.loadMoreFeed);

  const preferences = usePreferencesStore((s) => s.preferences);
  const prefsLoaded = usePreferencesStore((s) => s.loaded);
  const loadPreferences = usePreferencesStore((s) => s.loadPreferences);

  const trainingItems = useTrainingStore((s) => s.items);
  const trainingLoaded = useTrainingStore((s) => s.loaded);
  const loadTraining = useTrainingStore((s) => s.loadTraining);

  const user = useAuthStore((s) => s.user);
  const masteredSlugs = useTrainingStore((s) => s.masteredSlugs);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const restoredScroll = useRef(false);

  const visibleDialogs = useMemo(() => {
    const mastered = masteredSlugs();
    if (mastered.size === 0) return feedDialogs;
    return feedDialogs.filter((d) => !mastered.has(d.slug));
  }, [feedDialogs, masteredSlugs]);

  useEffect(() => {
    if (!prefsLoaded) loadPreferences();
  }, [prefsLoaded, loadPreferences]);

  useEffect(() => {
    if (!trainingLoaded) loadTraining();
  }, [trainingLoaded, loadTraining]);

  useEffect(() => {
    if (!coverage) loadCoverage();
  }, [coverage, loadCoverage]);

  useEffect(() => {
    if (!coverage || !prefsLoaded) return;
    const langs = preferences.map((p) => p.language);
    loadFeed(langs);
  }, [coverage, prefsLoaded, preferences, loadFeed]);

  useEffect(() => {
    if (visibleDialogs.length > 0 && !restoredScroll.current) {
      restoredScroll.current = true;
      const saved = sessionStorage.getItem(SCROLL_KEY);
      if (saved) {
        requestAnimationFrame(() => window.scrollTo(0, Number(saved)));
      }
    }
  }, [visibleDialogs.length]);

  useEffect(() => {
    const onScroll = () => sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && feedHasMore && !feedLoadingMore) {
        loadMoreFeed();
      }
    },
    [feedHasMore, feedLoadingMore, loadMoreFeed],
  );

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleObserver, { rootMargin: "400px" });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleObserver]);

  const emailUser = useAuthStore((s) => s.emailUser);
  const isSignedUp = !!(user || emailUser);
  const showPrefsHint = isSignedUp && preferences.length === 0;

  return (
    <div
      className="min-h-screen"
      style={{ background: "linear-gradient(180deg, #0f172a 0%, #1a1a2e 100%)" }}
    >
      <div className="max-w-md mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-white">Home</h1>
          {isSignedUp && (
            <Link
              to="/preferences"
              className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              Language Settings
            </Link>
          )}
        </div>

        {trainingItems.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3">
              My Training
            </h2>
            <div className="space-y-2">
              {trainingItems.slice(0, 5).map((item) => (
                <Link
                  key={item.slug}
                  to={`/dialog/${item.slug}`}
                  className="flex items-center gap-3 bg-th-surface border border-th-border rounded-lg p-3 hover:border-indigo-500/40 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">{item.title}</div>
                    <div className="text-xs text-slate-500">
                      {getLanguageName(item.languageCode)} · {item.level} · {item.speakers}
                    </div>
                  </div>
                  <span className="text-indigo-400 text-xs font-medium flex-shrink-0">Continue</span>
                </Link>
              ))}
              {trainingItems.length > 5 && (
                <p className="text-xs text-slate-500 text-center mt-2">
                  +{trainingItems.length - 5} more
                </p>
              )}
            </div>
          </div>
        )}

        {showPrefsHint && (
          <div className="bg-indigo-950/40 border border-indigo-500/30 rounded-xl p-4 mb-6 text-center">
            <p className="text-sm text-slate-300 mb-2">
              Set your languages to personalize your feed.
            </p>
            <Link
              to="/preferences"
              className="inline-block px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              Choose Languages
            </Link>
          </div>
        )}

        {preferences.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-6">
            {preferences.map((p) => (
              <span
                key={p.language}
                className="px-3 py-1 rounded-full text-xs font-medium bg-indigo-600/20 text-indigo-300 border border-indigo-500/30"
              >
                {p.level ? `${p.level} ` : ""}{getLanguageName(p.language)}
              </span>
            ))}
          </div>
        )}

        {feedLoading && (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!feedLoading && visibleDialogs.length > 0 && (
          <div className="space-y-6">
            {visibleDialogs.map((d) => (
              <FeedCard key={d.id} dialog={d} />
            ))}
          </div>
        )}

        {!feedLoading && visibleDialogs.length === 0 && coverage && (
          <div className="text-center py-20 text-slate-500">
            No dialogs available. Try adjusting your language preferences.
          </div>
        )}

        <div ref={sentinelRef} className="h-1" />

        {feedLoadingMore && (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
}
